/* eslint-disable max-lines */
import {Debouncer, logger} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  HubLike,
  MovementTemplate,
  SandboxTree
} from 'yougile-model'
import {Sync} from '@client/sync'
import {SyncEvents, SyncUpdate} from '@client/sync-events'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {ContextObject, ValidationDetails} from '../context-object'
import {Item} from './item'

/**
 * @description
 * Allows to manipulate all YouGile objects (tasks, columns, boards, projects)
 * and describes behaviour common to all such objects.
 *
 * Projects, boards, columns and tasks are structured as some hierarchical
 * system similar to files and folders on your computer
 * (see “Object navigator” tab). “Items” allows to access and
 * manipulate them in this manner. So when you are addressing
 * some task or board from Items it gives you an “item variable”.
 * @descriptionRu
 * Позволяет манипулировать всеми объектами внутри YouGile (задачи, колонки,
 * доски, проекты) и описывает поведение по умолчанию для этих объектов.
 *
 * Проекты, борды, колонки и задачи структурированы в некую
 * иерархичную систему,
 * похожую на файловую систему компьютера
 * (смотри вкладку "Навигатор по объектам").
 *
 * "Items" позволяет получить доступ и управлять ими на этот манер.
 * И когда вы обращаетесь к какой-либо задаче или доске через Items,
 * вы получаете переменную типа item (см. “item variable”).
 */
export class Items extends ContextObject {
  private onUpdateDebouncer = new Debouncer(3000)
  public static override registered: Map<DataObjectId, Map<string, Items>> =
    new Map()
  private syncListener = 0

  /**
   * @description
   * Gets Yougile object by it’s id.
   * Yougile objects are: company, projects, boards, column and tasks.
   * You can find object’s id in “Object navigator” tab
   * @descriptionRu
   * Получает объект YouGile по его id.
   * Объекты YouGile: компания, проекты, доски, колонки, задачи.
   * Вы можете найти id объектов во вкладке "Навигатор по объектам".
   * @code
   * Items.get('');
   * @example
   * // getting a task and assigning it to variable “t” for later use
   * var t = Items.get('bb12a559-81ba-40ad-adda-81f2d017e71b');
   *
   * // appending “!” to the task’s name
   * t.name = t.name + '!';
   */
  public get(id: DataObjectId): Item | undefined {
    const entity = this.context.box.get(id)
    if (!entity || entity.dataType !== 'Hub') {
      logger.error('Item is not found by id', id)
      return undefined
    }
    return new Item(id, this.context.box)
  }

  /**
   * @code
   * Items.onAdd = function (object) {
   *
   * };
   * @example
   * Items.onAdd = function (obj) {
   *   if (obj.type === 'Task') { // only when a task was added
   *     // changing a name of the new task
   *     obj.name = obj.name + ' ...';
   *   }
   * };
   * @description
   * This function is called after a new object was added
   * (task, column, board, project).
   * @descriptionRu
   * Эта функция вызывается сразу после добавления нового объекта в систему
   * (задачи, колонки, доски, проекта).
   */
  public onAdd?: (newObject: Item) => boolean

  /**
   * @code
   * Items.onBeforeAdd = function (location, name) {
   *   return true;
   * };
   * @example
   * Items.onBeforeAdd = function (a, s) {
   *   if (a.type === 'Column') { // only when a task is added to a column
   *     // prevent task addition with name “test”
   *     if (s === 'test') {
   *       return false;
   *     }
   *   }
   *   // we return true because we want to proceed addition
   *   // if it is needed to prevent addition, then return false
   *   return true;
   * };
   * @description
   * This function is called before the addition of new object
   * (task, column, board, project). First argument is a location where
   * new object is added (e.g. if a task is added then it is a column), second
   * argument is a name of the new object.
   * If this function return false, then new object is not added.
   * @descriptionRu
   * Эта функция вызывается, перед добавлением нового объекта в систему.
   * Первый аргумент — куда добавляется новый объект (напр. если добавляется
   * задача, то в первом аргументе колонка, куда добавляется), второй аргумент
   * — имя нового объекта. Если функция возвращает false, добвление
   * отменяется.
   */
  public onBeforeAdd?: (location: Item, name: string) => boolean

  /**
   * @code
   * Items.onBeforeDelete = function (location) {
   *   return true;
   * };
   * @example
   * Items.onBeforeDelete = function (a) {
   *   if (a.type === 'Column') { // only when a task is added to a column
   *     // prevent task deletion with name “test”
   *     if (s === 'test') {
   *       return false;
   *     }
   *   }
   *   // we return true because we want to proceed deletion
   *   // if it is needed to prevent deletion, then return false
   *   return true;
   * };
   * @description
   * This function is called before the deletion of new object
   * (task). First argument is an item that is deleted
   * If this function return false, then new object is not deleted.
   * @descriptionRu
   * Эта функция вызывается, перед удалением нового объекта в систему.
   * Первый аргумент — объект, который будет удален. Если функция возвращает
   * false, то новый объект не удаляется.
   */
  public onBeforeDelete?: (item: Item) => boolean

  /**
   * @code
   * Items.onBeforeUpdateSubtasksList = function (item) {
   *   return true;
   * };
   * @description
   * This function is called before the subtasks list is updated.
   * If this function returns false, then the subtasks list is not updated.
   * @descriptionRu
   * Эта функция вызывается, перед обновлением списка подзадач.
   * Если функция возвращает false, то список подзадач не обновляется.
   */
  public onBeforeUpdateSubtasksList?: (item: Item) => boolean

  /**
   * @code
   * Items.onRename = function (object, newName) {
   *   return true;
   * };
   * @example
   * Items.onRename = function (obj, n) {
   *   if (obj.type === 'Task') { // only for task renaming
   *     obj.name = n + '!!!';
   *     // return false to stop the default action — renaming the
   *     // object to a name specified by user (n variable in our case)
   *     return false;
   *   }
   *   // make the default action in all other cases
   *   return true;
   * };
   * @description
   * This function is called when some object is renamed (task, column,
   * board, project).
   * @descriptionRu
   * Эта функиця вызывается, когда какой-нибудь объект переименовывается
   * (задача, колонка, доска или проект).
   */
  public onRename?: (item: Item, newName: string) => boolean

  /**
   * @code
   * Items.onDelete = function (object) {
   *   return true;
   * };
   * @example
   * Items.onDelete = function (object) {
   *   // preventing to delete object which name starts with 'Important'
   *   if (object.name.slice(0, 9) === 'Important') {
   *     Notify.warn('You’re not allowed to delete important things!');
   *     return false;
   *   }
   *   // otherwise proceed with deletion
   *   return true;
   * };
   * @description
   * This function is called when user deletes some object (task, column,
   * board or project).
   * @descriptionRu
   * Эта функция вызывается, когда юзер удаляет какой-нибудь объект (задачу,
   * колонку, доску или проект).
   */
  public onDelete?: (item: Item) => boolean

  /**
   * @code
   * Items.onAfterDelete = function (object) {
   *   return true;
   * };
   * @description
   * This function is called when user deletes some object (task, column,
   * board or project) after it was deleted.
   * @descriptionRu
   * Эта функция вызывается, когда юзер удаляет какой-нибудь объект (задачу,
   * колонку, доску или проект) после того, как он его удалил.
   */
  public onAfterDelete?: (item: Item) => boolean

  /**
   * @code
   * Items.onAfterDelete = function (object) {
   *   return true;
   * };
   * @description
   * This function is called when user completes some task
   * @descriptionRu
   * Эта функция вызывается, когда юзер выполняет задачу.
   */
  public onComplete?: (item: Item) => boolean

  /**
   * @code
   * Items.onMakeArchived = function (object) {
   *   return true;
   * };
   * @description
   * This function is called when the user places an object into the archive. (task, column,
   * board or project).
   * @descriptionRu
   * Эта функция вызывается, когда юзер помещает в архив какой-нибудь объект (задачу,
   * колонку, доску или проект).
   */
  public onChangeArchived?: (item: Item) => boolean

  public onChangeDeadline?: (item: Item) => boolean

  /**
   * @code
   * Items.onClick = function (object) {
   *   return true;
   * };
   * @example
   * Items.onClick = function (object) {
   *   Notifier.success('You’ve clicked ' + object.name);
   *   return true;
   * };
   * @description
   * This function is called when a user clicks on task, column header,
   * board tab or project.
   * @descriptionRu
   * Эта функция вызывается, когла пользователь щелкает на задачу, заголовок
   * колонки, вкладку доски или на проект.
   */
  public onClick?: (item: Item) => boolean

  /**
   * @code
   * Items.onList = function (object, children) {
   *   return children;
   * };
   * @example
   * Items.onList = function (obj, arr) {
   *   // obj - is a container which child objects are going to be listed
   *   // arr - is its actual children objects
   *   if (obj.type === 'Column') { // in case of task list in a column
   *     // here obj is a column and arr is all its tasks
   *
   *     // we return only 5 elements from the array of tasks,
   *     // this result is going to be shown to the user
   *     // so user is going to see no more than 5 tasks in every column
   *     return arr.slice(0, 5);
   *   }
   *   // otherwise we return default
   *   return arr;
   * };
   * @description
   * This function is called when system is trying to show the list of
   * object inside some object. For example when it draws a list of
   * tasks inside of a column, or a list of columns inside of a
   * board and so on.
   * @descriptionRu
   * Эта функция вызывается, когда система показывает список объектов внутри
   * какого-то объекта. Например, когда отрисовывается список задач внутри
   * колонки или список колонок внутри доски и так далее.
   */
  public onList?: (item: Item, items: Item[]) => Item[]

  /**
   * @code
   * Items.onMove = function (object, from, to) {
   *   return true;
   * };
   * @example
   * Items.onMove = function (obj, from, to) {
   *   // getting some board by id
   *   var brd = Items.get('... board id here');
   *   // suppose we want to disable moving tasks in this board
   *
   *   // so we check if parent object of from is this board
   *   // if so then from object is some column inside this board
   *   // and that’s why obj is some task inside this column which user is
   *   // trying to move somewhere. So we return false to prevent it.
   *   if (from.up().id === brd.id) {
   *     Notifier.warn('Moving tasks inside ' +
   *       brd.name + ' board is disallowed');
   *     return false;
   *   }
   *   return true;
   * };
   * @description
   * This function is called when user moves some object from one location to
   * another (for example moves task from one column to another).
   * @descriptionRu
   * Эта функция вызывается, когда пользователь перемещает объект из одного
   * места в другое (например, перемещает задачу из одной колонки в другую).
   */
  public onMove?: (item: Item, from: Item, to: Item) => boolean

  /**
   * @code
   * Items.onMoveByTemplate = function (object, template) {
   *   return new Promise((resolve) => { ... });
   * };
   * @description
   * This function is called when the user moves an item from one location to another by template.
   * It should return a promise that resolves to a boolean value. If the promise resolves to true, the item is moved.
   * @descriptionRu
   * Эта функция вызывается, когда пользователь перемещает объект из одного места в другое по шаблону.
   * Она должна возвращать промис, который разрешается до логического значения. Если промис разрешается в true, объект перемещается.
   * @remarks
   * This function acts as breaking, if it is not defined, the item will be moved, but if it is defined, the item will be moved only if (and when) the promise resolves to true.
   * Therefor, it not only allows to prevent the item from being moved, but also to perform some asynchronous operations before moving the item (e.g. show confirmation dialog).
   */
  public onBeforeMoveByTemplate?: (
    item: Item,
    template: MovementTemplate
  ) => Promise<boolean>

  /**
   * @code
   * Items.onUpdate = function (obj) {
   *
   * };
   * @description
   * This function is called when some item (project, board, column, task)
   * was updated by the current user or by other connected user
   * (remote update).
   * If chat was updated than this function is also called and the
   * corresponding task is passed to it.
   * @descriptionRu
   * Эта функция вызывается когда какой-то объект системы был обновлён
   * (проект, доска, колонка, задача). Учитываются не только обновления
   * текущим пользователем, но и другие обновления, приходящие с сервера.
   * Если произошло обновление чата, то вызывается эта функция с
   * соответсвующей задачей в качестве аргумента.
   * @example
   * var column = Items.get('...');
   * // let’s store a number of tasks
   * // in this column in some variable
   * var tasksCount = column.list().length;
   *
   * // if we want to recalculate tasksCount
   * // on changes then we can do it as follows
   * Items.onUpdate = function (obj) {
   *   if (obj.id === column.id) {
   *     tasksCount = column.list().length;
   *   }
   * };
   */
  public onUpdate?: (item: Item) => void

  /**
   * @code
   * Items.isHidden = function (object) {
   *   return false;
   * };
   * @example
   * Items.isHidden = function (x) {
   *   // we do not want to show some column to user Mike
   *   if (Current.user.email === 'mike@example.com') { // better to compare id
   *     if (x.id === '... here goes column id') {
   *       return true;
   *     }
   *   }
   *   return false;
   * };
   * @description
   * If this function returns true for some object in some case, then this
   * object is not shown.
   * Если эта функция возвращает true для какого-нибудь объекта в какой-нибудь
   * ситуации, то этот объект не отображается.
   */
  public isHidden?: (item: Item) => boolean

  /**
   * @code
   * Items.allowDelete = function (object) {
   *   return true;
   * };
   * @example
   * Items.allowDelete = function (object) {
   *   if (object.type === 'Column') {
   *     // hide all delete buttons from all columns
   *     return false;
   *   }
   *   return true;
   * };
   * @description
   * If this function returns false for some object in some case
   * then delete button is not shown for this object
   * (task, column, board, project).
   * @descriptionRu
   * Если эта функция возвращает false для какого-нибудь объекта в
   * какой-нибудь ситуации, то кнопка удаления не показывается для
   * этого объекта (задачи, колонки, доски или проекта).
   */
  public allowDelete?: (item: Item) => boolean

  /**
   * @code
   * Items.allowAdd = function (to) {
   *   return true;
   * };
   * @example
   * Items.allowAdd = function (a) {
   *   // remove “Add a new task” button from all columns
   *   if (a.type === 'Column') {
   *     return false;
   *   }
   *   return true;
   * };
   * @description
   * If this function returns false for some object then add button is not
   * shown inside this object.
   * @descriptionRu
   * Если эта функция возвращает false для какого-нибудь объекта, тогда
   * кнопка добавления не отображается внутри этого объекта.
   */
  public allowAdd?: (item: Item) => boolean

  /**
   * @code
   * Items.allowRename = function (object) {
   *   return true;
   * };
   * @example
   * Items.allowRename = function (object) {
   *   // disallow renaming to a specific user
   *   if (Current.user.id === '... user id here') {
   *     return false;
   *   }
   *   return true;
   * };
   * @description
   * If this function returns false for some object then it can’t be renamed
   * from interface.
   * @descriptionRu
   * Если эта функция возвращает false для некоторого объекта, тогда он не
   * может быть переименован через интерфейс.
   */
  public allowRename?: (item: Item) => boolean

  /**
   * @code
   * Items.allowDrag = function (object) {
   *   return true;
   * };
   * @example
   * Items.allowDrag = function (object) {
   *   // disallow dragging to a specific user
   *   if (Current.user.id === '... user id here') {
   *     return false;
   *   }
   *   return true;
   * };
   * @description
   * If this function returns false for some object then it can’t
   * be dragged (task, board, column or project).
   * @descriptionRu
   * Если эта функция возвращает false для объекта, то его нельзя
   * перетаскивать.
   */
  public allowDrag?: (item: Item) => boolean

  public onUpdateSubtasksList?: (item: Item) => boolean

  /**
   * @code
   * Items.getBoards(projectId)
   * @description
   * Returns an array of boards for the specified project.
   * @descriptionRu
   * Возвращает массив досок для указанного проекта.
   * @param projectId
   */
  getBoards(projectId: DataObjectId): Item[] {
    const box = Sync.getCurrentSandbox()
    const project = box.get(projectId) as Hub
    return Project.listBoards(project, true).map(x => Items.fromHub(x))
  }

  /**
   * @code
   * Items.getColumns(boardItem);
   * @description
   * Returns an array of columns for the specified board.
   * @descriptionRu
   * Возвращает массив колонок для указанной доски.
   * @param boardId
   */
  getColumns(boardId: DataObjectId) {
    const box = Sync.getCurrentSandbox()
    const boardHub = box.get(boardId) as Hub
    return Board.listColumns(boardHub, {sorted: true, noMirrors: true}).map(x =>
      Items.fromHub(x)
    )
  }

  /**
   * @code
   * Items.getColumnTasks(columnId);
   * @description
   * Returns an array of tasks for the specified column.
   * @descriptionRu
   * Возвращает массив задач для указанной колонки.
   * @param columnId
   */
  getColumnTasks(columnId: DataObjectId) {
    const box = Sync.getCurrentSandbox()
    const column = box.get(columnId) as Hub
    return Column.listTasks(column, false, {
      sorted: true,
      withoutArchive: true
    }).map(x => Items.fromHub(x))
  }

  public static add(newItem: Item | DataObjectId): boolean {
    const tmp = Items.resolve(newItem)

    return Items.forEachCurrent((i: Items) => {
      if (i.onAdd) {
        i.onAdd(tmp)
      }
      return true
    })
  }

  public static beforeAdd(item: Item | DataObjectId, name: string): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onBeforeAdd ? i.onBeforeAdd(tmp, name) : true
    )
  }

  public static rename(item: Item | DataObjectId, newName: string): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onRename ? i.onRename(tmp, newName) : true
    )
  }

  public static beforeDelete(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onBeforeDelete ? i.onBeforeDelete(tmp) : true
    )
  }

  public static beforeUpdateSubtasksList(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onBeforeUpdateSubtasksList ? i.onBeforeUpdateSubtasksList(tmp) : true
    )
  }

  public static complete(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onComplete ? i.onComplete(tmp) : true
    )
  }

  public static delete(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onDelete ? i.onDelete(tmp) : true
    )
  }

  public static afterDelete(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onAfterDelete ? i.onAfterDelete(tmp) : true
    )
  }

  public static changeArchived(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onChangeArchived ? i.onChangeArchived(tmp) : true
    )
  }

  public static changeDeadline(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onChangeDeadline ? i.onChangeDeadline(tmp) : true
    )
  }

  public static click(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }
    return Items.forEachCurrent((i: Items) =>
      i.onClick ? i.onClick(tmp) : true
    )
  }

  public static list(
    item: Item | DataObjectId,
    items: ReadonlyArray<Item | DataObjectId>
  ): Item[] {
    const tmp = Items.resolve(item)
    const tmps = items.map(Items.resolve)

    if (!tmp) {
      return []
    }

    let curr = tmps
    Items.forEachCurrent((i: Items) => {
      if (i.onList) {
        curr = i.onList(tmp, curr)
      }
      return true
    })
    return curr
  }

  /**
   * The method should be called when the user moves an item from one location to another.
   * @param item
   * @param from
   * @param to
   */
  public static move(
    item: Item | DataObjectId,
    from: Item | DataObjectId,
    to: Item | DataObjectId
  ): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    const fromTmp = Items.resolve(from)
    const toTmp = Items.resolve(to)

    return Items.forEachCurrent((i: Items) =>
      i.onMove ? i.onMove(tmp, fromTmp, toTmp) : true
    )
  }

  /**
   * Checks if the item can be moved from one location to another. Method emits onMove event for all current items
   * @param item
   * @param from
   * @param to
   * @return ValidationDetails — an object with a boolean property `passed` that indicates whether the item can
   * be moved and additional information if needed.
   */
  public static canMove(
    item: Item | DataObjectId,
    from: Item | DataObjectId,
    to: Item | DataObjectId
  ): ValidationDetails {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return {
        success: true
      }
    }

    const fromTmp = Items.resolve(from)
    const toTmp = Items.resolve(to)

    return Items.validateAllCurrent((i: Items) =>
      i.onMove ? i.onMove(tmp, fromTmp, toTmp) : true
    )
  }

  /**
   * The method should be called when the user moves an item from one location to another by template.
   * It should return a promise that resolves to a boolean value. If the promise resolves to true, the item is moved.
   * @param item
   * @param template
   */
  public static async beforeMoveByTemplate(
    item: Item | DataObjectId,
    template: MovementTemplate
  ): Promise<boolean> {
    const itemThing = Items.resolve(item)
    const contextObjects = Items.getCurrentAsArray()
    for (const i of contextObjects) {
      const instance = i as Items
      if (instance.onBeforeMoveByTemplate) {
        const nextResult = await instance.onBeforeMoveByTemplate(
          itemThing,
          template
        )
        if (!nextResult) {
          return false
        }
      }
    }
    return true
  }

  public static updateSubtasksList(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.onUpdateSubtasksList ? i.onUpdateSubtasksList(tmp) : true
    )
  }

  public static isHidden(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return false
    }
    return !Items.forEachCurrent((i: Items) =>
      i.isHidden ? !i.isHidden(tmp) : true
    )
  }

  public static allowDelete(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }
    return Items.forEachCurrent((i: Items) =>
      i.allowDelete ? i.allowDelete(tmp) : true
    )
  }

  public static allowAdd(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }
    return Items.forEachCurrent((i: Items) =>
      i.allowAdd ? i.allowAdd(tmp) : true
    )
  }

  public static allowRename(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.allowRename ? i.allowRename(tmp) : true
    )
  }

  public static allowDrag(item: Item | DataObjectId): boolean {
    const tmp = Items.resolve(item)

    if (!tmp) {
      return true
    }

    return Items.forEachCurrent((i: Items) =>
      i.allowDrag ? i.allowDrag(tmp) : true
    )
  }

  /**
   * Utility methods.
   */

  private static resolve(item: Item | DataObjectId): Item {
    return item instanceof Item ? item : Items.get(item)
  }

  public static get(id: DataObjectId): Item {
    const box = Sync.getCurrentSandbox()
    if (box.companyId !== globals.getCompanyId()) {
      throw new Error(
        `Company mismatch. Companies ${
          box.companyId
        }, ${globals.getCompanyId()}`
      )
    }
    return new Item(id, box)
  }

  public static toHub(item: Item): Hub {
    return item.box.get(item.id) as Hub
  }

  public static fromHub(h: HubLike): Item {
    const box = Sync.get(h)
    if (box.companyId !== globals.getCompanyId()) {
      throw new Error(
        `Company mismatch. Companies ${
          box.companyId
        }, ${globals.getCompanyId()}`
      )
    }
    return new Item(h.id, box)
  }

  public override afterLoad(): void {
    const box = this.context.box
    let changes: [DataObjectId, SandboxTree][] = []
    this.syncListener = SyncEvents.addListener(
      this.context.box.companyId,
      '*',
      res => {
        if (this.onUpdate) {
          res.forEach((u: SyncUpdate) => {
            u.ids.forEach((id: DataObjectId) => {
              const obj = box.get(id)
              if (
                obj &&
                obj.dataType === 'Hub' &&
                !obj.title.startsWith('.') &&
                this.onUpdate
              ) {
                if (!changes.find(c => c[0] === id)) {
                  changes.push([id, box])
                }
              }
            })
          })

          const selfOnUpdate = this.onUpdate

          this.onUpdateDebouncer.runLatest(() => {
            const preChanges = changes
            changes = []

            for (const [id, box] of preChanges) {
              selfOnUpdate(new Item(id, box))
            }
          })
        }
      },
      true
    )
  }

  public override beforeUnload(): void {
    SyncEvents.removeListener(this.syncListener)
  }
}
